import {
    AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EmbeddedViewRef, Input,
    NgZone,
    OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef
} from "@angular/core";
import { IAspectRatio } from "@karma-games/angular-devkit/src/lib/std/globals";
import { distinctUntilChanged, firstValueFrom, skip, Subject, timer } from "rxjs";
import { GameResizeService } from "@karma-games/angular-devkit/src/lib/services/game-resize";
import { CrocoGameApp } from "./game-app/croco-game-app";
import { CrocoGameAppFactory } from "./game-app/croco-game-app.factory";
import { ERoundState, IRoundGetStateData } from "../core/round/interfaces";
import { GetRoundStateUseCase } from "../core/round/domain/get-round-state.use-case";
import { takeUntil } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { GetRoundMultiplierUseCase } from "../core/round/domain/get-round-multiplier.use-case";
import gsap from "gsap";
import { GetConfigUseCase } from "../core/config/get-config.use-case";
import { ILobbyConfig } from "../core/config/interfaces";
import { GetRoundInitialStateUseCase } from "../core/round/domain/get-round-initial-state.use-case";
import { SoundService } from "@karma-games/angular-devkit/src/lib/modules/sound";
import {
    GetForceCanvasEnabledUsecase,
    GetSoundsEnabledUseCase,
    GetNoAnimationEnabledUsecase, GetBgMusicEnabledUseCase
} from "@karma-games/angular-devkit/src/lib/modules/settings";
import { CrocoScreenGlowGameApp } from "./game-app/croco-screen-glow-app";
import { ECrocoGameApp, ESignAnimationStates } from "./game-app/enums";
import { CrocoGameAudio } from "./game-app/croco-game-audio";
import { ICustomLoaderData } from "@karma-games/angular-devkit/src/lib/interfaces";
import { buttonLoaderImage } from "../shared/constants/button-loader-image-base64";
import { GameMultiplierDirective } from "./game-multiplier.directive";
import { GameNumberColorDirective } from "./game-number-color.directive";

@Component({
    selector: "game",
    templateUrl: "./game.component.html",
    styleUrls: ["./game.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        CrocoGameAppFactory,
        GetSoundsEnabledUseCase
    ]
})
export class GameComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(GameMultiplierDirective, { static: false, read: GameMultiplierDirective }) gameMultiplierDirective: GameMultiplierDirective | undefined = undefined;
    @ViewChild(GameNumberColorDirective, { static: false, read: GameNumberColorDirective }) gameNumberColorDirective: GameNumberColorDirective | undefined = undefined;
    @ViewChild("game", { static: true }) game: ElementRef = { nativeElement: undefined };
    @ViewChild("gameLoaderBar", {
        static: false,
        read: ElementRef
    }) gameLoaderBar?: ElementRef = { nativeElement: undefined };
    @ViewChild("multiplierParticleContainer", {
        static: false,
        read: ViewContainerRef
    }) multiplierParticleContainer?: ViewContainerRef;
    @ViewChild("multiplierParticle", { static: false, read: TemplateRef }) multiplierParticle?: TemplateRef<any>;
    @ViewChild("multiplierParticleBig", { static: false, read: TemplateRef }) multiplierParticleBig?: TemplateRef<any>;
    public roundState: ERoundState | undefined = undefined;
    public ERoundState: typeof ERoundState = ERoundState;

    @Input() set layoutChange(_value: any) {
        this.layoutChange$.next();
    }

    public loaderOn: boolean = true;
    public noAnimationEnabled: boolean = false;
    public buttonLoaderImage: ICustomLoaderData = {
        url: buttonLoaderImage,
        forceRotate: true,
        additionalStyles: {
            height: "4em",
            width: "4em"
        }
    };
    private crocoGameApp: CrocoGameApp | undefined = undefined;
    private crocoScreenGlowGameApp: CrocoScreenGlowGameApp | undefined = undefined;
    private layoutChange$: Subject<void> = new Subject();
    private aspectRatio: IAspectRatio = { portrait: .56, landscape: 1.78 };
    private destroy$: Subject<void> = new Subject();
    private particleEmbeddedView: EmbeddedViewRef<any> | undefined = undefined;
    private particleBigEmbeddedView: EmbeddedViewRef<any> | undefined = undefined;
    private defaultIdleStateMs: number = 6000;
    private idleBarAnimation: gsap.core.Timeline | undefined = undefined;
    private previousShownSignAnimationState: ESignAnimationStates | undefined = undefined;
    private crocoGameAudio: CrocoGameAudio | undefined = undefined;

    constructor(private gameResizeService: GameResizeService,
                private gameAppFactory: CrocoGameAppFactory,
                private getRoundStateUseCase: GetRoundStateUseCase,
                private getRoundMultiplierUseCase: GetRoundMultiplierUseCase,
                private cdr: ChangeDetectorRef,
                private getRoundInitialStateUseCase: GetRoundInitialStateUseCase,
                private getConfigUseCase: GetConfigUseCase,
                private soundService: SoundService,
                private getForceCanvasEnabledUsecase: GetForceCanvasEnabledUsecase,
                private getNoAnimationEnabledUsecase: GetNoAnimationEnabledUsecase,
                private getSoundEnabledUseCase: GetSoundsEnabledUseCase,
                private getBgMusicEnabledUseCase: GetBgMusicEnabledUseCase,
                private ngZone: NgZone) {
        gsap.ticker.lagSmoothing(0);
    }

    async ngOnInit(): Promise<void> {
        this.crocoGameAudio = new CrocoGameAudio(this.soundService);
        this.getBgMusicEnabledUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe((enabled: boolean) => {
                this.crocoGameAudio?.onGameSoundSettingChange(enabled);
            });
        this.getSoundEnabledUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe((enabled: boolean) => {
                this.crocoGameAudio?.onSoundSettingChange(enabled);
            });
        try {
            const config: ILobbyConfig | undefined = await this.getConfigUseCase.execute();
            if (config) {
                this.defaultIdleStateMs = config.idleDuration;
            }
        } catch (e) {
            console.error(e);
        }
    }

    async ngAfterViewInit() {
        try {
            const startState: IRoundGetStateData = await firstValueFrom(this.getRoundInitialStateUseCase.execute());
            this.roundState = await firstValueFrom(this.getRoundStateUseCase.execute());

            if (startState.nextRoundStartsAt - this.getCurrentTime() > 0) {
                this.roundState = ERoundState.Idle;
                this.cdr.detectChanges();
                this.triggerProgressBarAnimation(ERoundState.Idle, startState.nextRoundStartsAt);
            } else {
                this.triggerProgressBarAnimation(ERoundState.Idle);
            }
        } catch (e) {
            console.error(e);
        }
        combineLatest([this.getForceCanvasEnabledUsecase.execute(), this.getNoAnimationEnabledUsecase.execute()])
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: [forceCanvas: boolean, noAnimation: boolean]) => {
                if (!data[1]) {
                    this.init(data[0]);
                } else {
                    this.destroyGame();
                    this.loaderOn = false;
                }
                this.noAnimationEnabled = data[1];
                this.cdr.detectChanges();
            });
        this.getRoundStateUseCase.execute().pipe(skip(1), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(this.changeAnimState);
        this.getRoundMultiplierUseCase.execute().pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(this.onMultiplierUpdate);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    private destroyGame(): void {
        if (this.crocoGameApp && this.crocoScreenGlowGameApp) {
            this.crocoGameApp?.destroy(true);
            this.crocoScreenGlowGameApp?.destroy(true);
            this.crocoGameApp = undefined;
            this.crocoScreenGlowGameApp = undefined;
            this.cdr.detectChanges();
        }
    }

    private async init(forceCanvas: boolean): Promise<void> {
        this.destroyGame();
        this.crocoGameApp = <CrocoGameApp>this.gameAppFactory.createApp({
            width: 800,
            height: 450,
            forceCanvas: forceCanvas
        });
        this.crocoScreenGlowGameApp = <CrocoScreenGlowGameApp>this.gameAppFactory.createApp({
                width: 800,
                height: 450,
                transparent: true,
                forceCanvas: forceCanvas
            },
            ECrocoGameApp.CrocoScreenGlowGameApp);

        this.crocoGameApp.initialize(this.game.nativeElement, this.roundState);
        this.crocoScreenGlowGameApp.initialize(this.game.nativeElement, this.roundState);
        this.gameResizeService.addResizeListener(this.crocoGameApp.view, this.aspectRatio, [this.layoutChange$.asObservable()]);
        this.loaderOn = false;
        this.cdr.detectChanges();
    }

    private getCurrentTime(): number {
        return Math.floor(Date.now() / 1000);
    }

    private changeAnimState = (roundState: ERoundState): void => {
        this.crocoGameApp?.changeState(roundState);
        this.crocoScreenGlowGameApp?.changeState(roundState);
        this.roundState = roundState;
        if (this.roundState === ERoundState.Idle) {
            this.previousShownSignAnimationState = undefined;
        }
        this.cdr.detectChanges();
        this.crocoGameAudio?.handleAnimationAudio(this.roundState);
        this.triggerProgressBarAnimation(this.roundState);
    };

    private triggerProgressBarAnimation(roundState: ERoundState, nextRoundStartsAt?: number): void {
        if (roundState === ERoundState.Idle && this.gameLoaderBar) {
            const nextRoundTime: number = nextRoundStartsAt ? nextRoundStartsAt - this.getCurrentTime() : 0;
            const secondsToRound: number | undefined = nextRoundTime > 0 ? nextRoundTime : undefined;

            this.idleBarAnimation = gsap.timeline()
                .set(this.gameLoaderBar.nativeElement, {
                    width: secondsToRound ? `${(secondsToRound * 1000 / this.defaultIdleStateMs) * 100}%` : "0%"
                })
                .to(this.gameLoaderBar.nativeElement, {
                    duration: !secondsToRound ? this.defaultIdleStateMs / 1000 : secondsToRound,
                    ease: "linear",
                    width: "100%",
                    onStart: () => this.cdr.detectChanges(),
                    onComplete: () => {
                        if (this.idleBarAnimation) {
                            this.idleBarAnimation.kill();
                        }
                        this.idleBarAnimation = undefined;
                    }
                });
        } else {
            if (this.idleBarAnimation) {
                this.idleBarAnimation.kill();
            }
            this.idleBarAnimation = undefined;
        }
    }

    private onMultiplierUpdate = (multiplier: number): void => {
        this.gameMultiplierDirective?.onMultiplierUpdate(multiplier);
        this.gameNumberColorDirective?.onMultiplierUpdate(multiplier);
        if (this.crocoGameApp) {
            this.crocoGameApp.triggerSignAnimation(this.getSignAnimationState(multiplier));
        }
        this.crocoGameAudio?.onMultiplierUpdate(multiplier);

        if (this.checkIfBigMultiplier(multiplier) && this.multiplierParticleContainer && this.multiplierParticleBig && !this.particleBigEmbeddedView) {
            this.particleBigEmbeddedView = this.multiplierParticleContainer?.createEmbeddedView(this.multiplierParticleBig);
            this.cdr.detectChanges();
            timer(1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.particleBigEmbeddedView!.destroy();
                    this.particleBigEmbeddedView = undefined;
                });

            this.crocoGameAudio?.multiplierSound(true);
        }

        if (this.checkIfSmallMultiplier(multiplier) && this.multiplierParticleContainer && this.multiplierParticle && !this.particleEmbeddedView) {
            this.particleEmbeddedView = this.multiplierParticleContainer?.createEmbeddedView(this.multiplierParticle);
            this.cdr.detectChanges();
            timer(1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.particleEmbeddedView!.destroy();
                    this.particleEmbeddedView = undefined;
                });
            this.crocoGameAudio?.multiplierSound();
        }
    };

    private checkIfSmallMultiplier(multiplier: number): boolean {
        return this.ngZone.runOutsideAngular(() => {
            return (multiplier >= 2 && multiplier <= 2.2) || (multiplier >= 5 && multiplier <= 5.2) ||
                (multiplier >= 10 && multiplier <= 10.2) || (multiplier >= 20 && multiplier <= 20.2);
        });
    }

    private checkIfBigMultiplier(multiplier: number): boolean {
        return this.ngZone.runOutsideAngular(() => {
            return (multiplier >= 50 && multiplier <= 51) || (multiplier >= 100 && multiplier <= 101) ||
                (multiplier >= 200 && multiplier <= 202) || (multiplier >= 300 && multiplier <= 303) ||
                (multiplier >= 500 && multiplier <= 505) || (multiplier >= 700 && multiplier <= 707) ||
                (multiplier >= 1000 && multiplier <= 1020) || (multiplier >= 1500 && multiplier <= 1520) ||
                (multiplier >= 2000 && multiplier <= 2020) || (multiplier >= 2500 && multiplier <= 2520) ||
                (multiplier >= 3000 && multiplier <= 3020) || (multiplier >= 3500 && multiplier <= 3520) ||
                (multiplier >= 4000 && multiplier <= 4020) || (multiplier >= 4500 && multiplier <= 4520) ||
                (multiplier >= 5000 && multiplier <= 5100) || (multiplier >= 6000 && multiplier <= 6100) ||
                (multiplier >= 7000 && multiplier <= 7100) || (multiplier >= 8000 && multiplier <= 8100) ||
                (multiplier >= 9000 && multiplier <= 9100) || (multiplier >= 9999 && multiplier <= 10000);
        });
    }

    private getSignAnimationState(multiplier: number): ESignAnimationStates | undefined {
        return this.ngZone.runOutsideAngular(() => {
            let animationState: ESignAnimationStates | undefined = undefined;
            if (multiplier >= 1.45 && multiplier < 3 && this.previousShownSignAnimationState !== ESignAnimationStates.x2) {
                animationState = ESignAnimationStates.x2;
                this.previousShownSignAnimationState = ESignAnimationStates.x2;
            } else if (multiplier >= 3 && multiplier < 6.1 && this.previousShownSignAnimationState !== ESignAnimationStates.x5) {
                animationState = ESignAnimationStates.x5;
                this.previousShownSignAnimationState = ESignAnimationStates.x5;
            } else if (multiplier >= 6.1 && multiplier < 38 && this.previousShownSignAnimationState !== ESignAnimationStates.x10) {
                animationState = ESignAnimationStates.x10;
                this.previousShownSignAnimationState = ESignAnimationStates.x10;
            } else if (multiplier >= 38 && multiplier < 80 && this.previousShownSignAnimationState !== ESignAnimationStates.x50) {
                animationState = ESignAnimationStates.x50;
                this.previousShownSignAnimationState = ESignAnimationStates.x50;
            } else if (multiplier >= 80 && multiplier < 170 && this.previousShownSignAnimationState !== ESignAnimationStates.x100) {
                animationState = ESignAnimationStates.x100;
                this.previousShownSignAnimationState = ESignAnimationStates.x100;
            } else if (multiplier >= 170 && multiplier < 255 && this.previousShownSignAnimationState !== ESignAnimationStates.x200) {
                animationState = ESignAnimationStates.x200;
                this.previousShownSignAnimationState = ESignAnimationStates.x200;
            } else if (multiplier >= 255 && multiplier < 344 && this.previousShownSignAnimationState !== ESignAnimationStates.x300) {
                animationState = ESignAnimationStates.x300;
                this.previousShownSignAnimationState = ESignAnimationStates.x300;
            } else if (multiplier >= 344 && multiplier < 438 && this.previousShownSignAnimationState !== ESignAnimationStates.x400) {
                animationState = ESignAnimationStates.x400;
                this.previousShownSignAnimationState = ESignAnimationStates.x400;
            } else if (multiplier >= 438 && multiplier < 528 && this.previousShownSignAnimationState !== ESignAnimationStates.x500) {
                animationState = ESignAnimationStates.x500;
                this.previousShownSignAnimationState = ESignAnimationStates.x500;
            } else if (multiplier >= 528 && multiplier < 625 && this.previousShownSignAnimationState !== ESignAnimationStates.x600) {
                animationState = ESignAnimationStates.x600;
                this.previousShownSignAnimationState = ESignAnimationStates.x600;
            } else if (multiplier >= 625 && multiplier < 725 && this.previousShownSignAnimationState !== ESignAnimationStates.x700) {
                animationState = ESignAnimationStates.x700;
                this.previousShownSignAnimationState = ESignAnimationStates.x700;
            } else if (multiplier >= 725 && multiplier < 825 && this.previousShownSignAnimationState !== ESignAnimationStates.x800) {
                animationState = ESignAnimationStates.x800;
                this.previousShownSignAnimationState = ESignAnimationStates.x800;
            } else if (multiplier >= 825 && multiplier < 925 && this.previousShownSignAnimationState !== ESignAnimationStates.x900) {
                animationState = ESignAnimationStates.x900;
                this.previousShownSignAnimationState = ESignAnimationStates.x900;
            } else if (multiplier >= 925 && multiplier < 1850 && this.previousShownSignAnimationState !== ESignAnimationStates.x1000) {
                animationState = ESignAnimationStates.x1000;
                this.previousShownSignAnimationState = ESignAnimationStates.x1000;
            } else if (multiplier >= 1850 && multiplier < 2800 && this.previousShownSignAnimationState !== ESignAnimationStates.x2000) {
                animationState = ESignAnimationStates.x2000;
                this.previousShownSignAnimationState = ESignAnimationStates.x2000;
            } else if (multiplier >= 2800 && multiplier < 3730 && this.previousShownSignAnimationState !== ESignAnimationStates.x3000) {
                animationState = ESignAnimationStates.x3000;
                this.previousShownSignAnimationState = ESignAnimationStates.x3000;
            } else if (multiplier >= 3730 && multiplier < 4750 && this.previousShownSignAnimationState !== ESignAnimationStates.x4000) {
                animationState = ESignAnimationStates.x4000;
                this.previousShownSignAnimationState = ESignAnimationStates.x4000;
            } else if (multiplier >= 4750 && multiplier < 5700 && this.previousShownSignAnimationState !== ESignAnimationStates.x5000) {
                animationState = ESignAnimationStates.x5000;
                this.previousShownSignAnimationState = ESignAnimationStates.x5000;
            } else if (multiplier >= 5700 && multiplier < 6700 && this.previousShownSignAnimationState !== ESignAnimationStates.x6000) {
                animationState = ESignAnimationStates.x6000;
                this.previousShownSignAnimationState = ESignAnimationStates.x6000;
            } else if (multiplier >= 6700 && multiplier < 7580 && this.previousShownSignAnimationState !== ESignAnimationStates.x7000) {
                animationState = ESignAnimationStates.x7000;
                this.previousShownSignAnimationState = ESignAnimationStates.x7000;
            } else if (multiplier >= 7580 && multiplier < 8570 && this.previousShownSignAnimationState !== ESignAnimationStates.x8000) {
                animationState = ESignAnimationStates.x8000;
                this.previousShownSignAnimationState = ESignAnimationStates.x8000;
            } else if (multiplier >= 8570 && multiplier < 9600 && this.previousShownSignAnimationState !== ESignAnimationStates.x9000) {
                animationState = ESignAnimationStates.x9000;
                this.previousShownSignAnimationState = ESignAnimationStates.x9000;
            } else if (multiplier >= 9600 && this.previousShownSignAnimationState !== ESignAnimationStates.x10000) {
                animationState = ESignAnimationStates.x10000;
                this.previousShownSignAnimationState = ESignAnimationStates.x10000;
            }

            return animationState;
        });
    }
}
